// src/console/pages/developer/developerNav.ts — where "Developer" lives in the
// console: the sidebar entry, its route rule, its tab strip and its paths.
//
// The developer portal is invite-only. This domain is where an operator issues
// the invitations, sees the workspaces they turned into, can stop one, and
// reviews a workspace's request for production access.
//
// ── REGISTERING THE DOMAIN (three edits outside this folder) ──────────────
// The console reads ONE nav source (src/console/nav.ts) for the sidebar, the
// router and the palette, and derives every sidebar gate from the route rules
// (src/config/adminFeatureAccess.ts):
//
//   1. nav.ts — DEVELOPER_DOMAIN in DOMAINS, and domain("developer") in the
//      "Customers" group after Businesses.
//   2. adminFeatureAccess.ts — DEVELOPER_ROUTE_RULE above the
//      `admin.dashboard` "/console/*" catch-all (first match wins). Without it
//      the domain is open to every operator and the sidebar shows it to all.
//   3. ConsoleApp.tsx — the routes, static before dynamic.
//
// The word is "workspace", not "organisation": in this console an organisation
// is a business account, and ⌘K already answers to it.
import type { ConsoleRoute, ConsoleSubroute } from "../../nav";
import type { AdminFeatureRouteRule } from "../../../config/adminFeatureAccess";
import type { SubNavItem } from "../../components/SubNav";

/** The capabilities behind /api/v2/admin/developer. Each stands alone. */
export const DEVELOPER_CAPABILITY = {
  /** read workspaces, invitations and the list of production requests */
  view: "developer.view",
  /** issue, resend and revoke invitations */
  invite: "developer.invite",
  /**
   * suspend and reinstate a workspace; put a hold on its production access,
   * and revoke that access (with the operator's own second factor)
   */
  suspend: "developer.suspend",
  /**
   * read a production request in full, take it into review, ask for changes
   * and reject it. Short of approval, which grants and has a key of its own.
   */
  activationReview: "developer.activation.review",
  /**
   * propose, approve and withdraw an approval. Approval is the one decision
   * that grants a workspace production access; it takes two operators, and
   * each acts with their own second factor. Also lifts a hold on a
   * workspace's production access, which gives access back.
   */
  activationApprove: "developer.activation.approve",
  /**
   * lift the lock on a member's second factor, or take it away. Granted on
   * purpose, apart from deciding who is in a workspace: that factor guards
   * the workspace's production keys. Each asks for the operator's own.
   */
  secondFactorReset: "developer.second_factor.reset",
} as const;

export const DEVELOPER_PATH = "/console/developer";
export const DEVELOPER_INVITES_PATH = "/console/developer/invites";
export const DEVELOPER_REQUESTS_PATH = "/console/developer/requests";

export const developerWorkspacePath = (workspaceId: string): string =>
  `${DEVELOPER_PATH}/workspaces/${encodeURIComponent(workspaceId)}`;

/** A member of a workspace, removed or not: who they are, and their second factor. */
export const developerMemberPath = (
  workspaceId: string,
  memberId: string
): string =>
  `${developerWorkspacePath(workspaceId)}/members/${encodeURIComponent(memberId)}`;

export const developerRequestPath = (requestId: string): string =>
  `${DEVELOPER_REQUESTS_PATH}/${encodeURIComponent(requestId)}`;

export const DEVELOPER_DOMAIN: ConsoleRoute = {
  id: "developer",
  label: "Developer",
  icon: "code",
  keywords:
    "developer portal developers workspace workspaces invite invitation invitations api sandbox partner integration",
};

/**
 * `developer.view` opens the domain. The writes inside it check their own
 * capability, so someone who can read the list cannot invite or suspend
 * unless they hold that key as well.
 */
export const DEVELOPER_ROUTE_RULE: AdminFeatureRouteRule = {
  featureKeys: [DEVELOPER_CAPABILITY.view],
  patterns: [DEVELOPER_PATH, `${DEVELOPER_PATH}/*`],
};

/**
 * Invitations and production requests get their own palette entries; the
 * sidebar keeps Developer lit.
 */
export const DEVELOPER_SUBROUTES: ConsoleSubroute[] = [
  {
    id: "developer/invites",
    parent: "developer",
    label: "Developer invitations",
    icon: "code",
    keywords:
      "invite developer invitation pending resend revoke workspace owner",
    featureKey: DEVELOPER_CAPABILITY.view,
  },
  {
    id: "developer/requests",
    parent: "developer",
    label: "Developer production requests",
    icon: "code",
    keywords:
      "production access request activation review queue approve reject changes live keys workspace",
    featureKey: DEVELOPER_CAPABILITY.view,
  },
];

export const DEVELOPER_NAV: SubNavItem[] = [
  {
    label: "Workspaces",
    to: DEVELOPER_PATH,
    featureKey: DEVELOPER_CAPABILITY.view,
  },
  {
    label: "Invitations",
    to: DEVELOPER_INVITES_PATH,
    featureKey: DEVELOPER_CAPABILITY.view,
  },
  {
    label: "Production requests",
    to: DEVELOPER_REQUESTS_PATH,
    featureKey: DEVELOPER_CAPABILITY.view,
  },
];
